/**
 * Phase 2b's session routes (CS r5a deliverable 145; DECISIONS 212), owner 2.C by stage, since
 * `exit-2b`, frozen: the run sheet, attendance and a session's captures (stage `s1`), and the
 * lifecycle and Tonight (stage `s2`). `:id` is the session, an owner pointer, so a reference id
 * answers 403 before the handler (`deny`); `sessions.items.create` names a target, the item's
 * document, and the captures' writes name theirs in `body.docIds[]`; each may be a reference document (`target-ok`: an SRD
 * monster on the run sheet). A
 * write declares 409 `row_conflict` where it takes `baseRv`, 409 `session_state` where the
 * session's status may refuse it (2.C says which status allows which write), 409
 * `document_trashed` where it names a document, and 422 `invalid_body` where the rows may refuse
 * what the schema admits. 1.T's `sessions.*` are unchanged: `sessions.update` never sets `running`.
 *
 * CS r5b (deliverables 162 and 164): every write is marked `changeBatch`, its header sent from 2.C
 * s3 (`HEADER_FROM_2C_S3`); `sessions.items.update`, `.move`, `.remove` and
 * `sessions.captures.remove` also declare 409 `document_trashed` and 422 `invalid_body` (DECISIONS
 * 338, so 2.C's fallbacks go); and 2.C s3's five routes follow: a capture's promote, a session's
 * sources (list, upload, remove) and its tentative reveals.
 */
import { DocIdParams } from '../dto/documents.js';
import { SessionV1 } from '../dto/ledger.js';
import { listOf } from '../dto/list.js';
import { BaseRvQueryV1 } from '../dto/query.js';
import { CapturePromotedV1, CapturePromoteV1, SESSION_REVEALS_MAX, SESSION_SOURCE_MAX_BYTES, SESSION_SOURCE_MEDIA_TYPES, SESSION_SOURCES_MAX, SessionAttendancePutV1, SessionAttendanceV1, SessionCaptureCreateV1, SessionCaptureParams, SessionCapturesQueryV1, SessionCaptureUpdateV1, SessionCaptureV1, SessionFinishedV1, SessionItemCreateV1, SessionItemMoveV1, SessionItemParams, SessionItemRemoveQueryV1, SessionItemUpdateV1, SessionItemV1, SessionRevealV1, SessionSourceParams, SessionSourceUploadFieldsV1, SessionSourceV1, SessionStartV1, TonightQueryV1, TonightV1, } from '../dto/sessions.js';
import { defineRoute } from '../route/define-route.js';
import { HEADER_FROM_2C_S3 } from './change-batch-senders.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_CAPTURE_PROMOTED, EXAMPLE_SESSION_REVEALS, EXAMPLE_SOURCE_SUMMARY, EXAMPLE_SOURCE_TRANSCRIPT, EXAMPLE_SOURCE_UPLOADED, EXAMPLE_TRANSCRIPT_FILE, } from './examples-2b-batches.js';
import { EXAMPLE_SESSION_ATTENDANCE, EXAMPLE_SESSION_CAPTURE, EXAMPLE_SESSION_CAPTURE_CREATED, EXAMPLE_SESSION_CAPTURE_UPDATED, EXAMPLE_SESSION_ENDED, EXAMPLE_SESSION_FINISHED, EXAMPLE_SESSION_ITEM_CREATED, EXAMPLE_SESSION_ITEM_MOVED, EXAMPLE_SESSION_ITEM_UPDATED, EXAMPLE_SESSION_ITEMS, EXAMPLE_SESSION_STARTED, EXAMPLE_TONIGHT, NEEDS_RUNNING_SESSION, } from './examples-sessions.js';
const ids = EXAMPLE_IDS;
const session3 = { id: ids.sessionSunkenChapel.id };
const sessions = { auth: 'session', status: 'frozen', since: 'exit-2b', owner: '2.C' };
const SESSION = ['params.id'];
/** The run sheet of a session, in position order (a bounded list). */
export const sessionsItemsList = defineRoute({
    id: 'sessions.items.list',
    method: 'GET',
    path: '/api/sessions/:id/items',
    params: DocIdParams,
    response: { kind: 'json', schema: listOf(SessionItemV1) },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's1',
    examples: {
        request: { params: session3 },
        response: { items: [...EXAMPLE_SESSION_ITEMS], next: null },
    },
});
/** Add an item: a scene, a document (a reference one included), a secret or a note. */
export const sessionsItemsCreate = defineRoute({
    id: 'sessions.items.create',
    method: 'POST',
    path: '/api/sessions/:id/items',
    params: DocIdParams,
    body: SessionItemCreateV1,
    response: { kind: 'json', schema: SessionItemV1, status: 201 },
    errors: { 409: ['session_state', 'document_trashed'], 422: ['invalid_body'] },
    reference: 'target-ok',
    docIds: { owner: SESSION, target: ['body.documentId'] },
    ...sessions,
    stage: 's1',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        request: {
            params: session3,
            body: {
                kind: 'document',
                documentId: ids.referenceGhoul.id,
                label: 'Ghouls in the crypt',
                estMinutes: 20,
            },
        },
        response: EXAMPLE_SESSION_ITEM_CREATED,
    },
});
/** Change an item's label, planned note, minutes or pin. */
export const sessionsItemsUpdate = defineRoute({
    id: 'sessions.items.update',
    method: 'PATCH',
    path: '/api/sessions/:id/items/:itemId',
    params: SessionItemParams,
    body: SessionItemUpdateV1,
    response: { kind: 'json', schema: SessionItemV1 },
    errors: { 409: ['row_conflict', 'session_state', 'document_trashed'], 422: ['invalid_body'] },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's1',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        request: {
            params: { ...session3, itemId: ids.sessionItemFloodScene.id },
            body: { baseRv: 1, plannedNote: 'Open on the bells, then the flood.', estMinutes: 35 },
        },
        response: EXAMPLE_SESSION_ITEM_UPDATED,
        consumes: true,
    },
});
/** Move an item after another, or first (a fractional key, as a link's position). */
export const sessionsItemsMove = defineRoute({
    id: 'sessions.items.move',
    method: 'POST',
    path: '/api/sessions/:id/items/:itemId/move',
    params: SessionItemParams,
    body: SessionItemMoveV1,
    response: { kind: 'json', schema: SessionItemV1 },
    errors: { 409: ['row_conflict', 'session_state', 'document_trashed'], 422: ['invalid_body'] },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's1',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        request: {
            params: { ...session3, itemId: ids.sessionItemChapelNote.id },
            body: { baseRv: 1, afterItemId: null },
        },
        response: EXAMPLE_SESSION_ITEM_MOVED,
        consumes: true,
    },
});
/** Remove an item (soft: `deleted_at`). */
export const sessionsItemsRemove = defineRoute({
    id: 'sessions.items.remove',
    method: 'DELETE',
    path: '/api/sessions/:id/items/:itemId',
    params: SessionItemParams,
    query: SessionItemRemoveQueryV1,
    response: { kind: 'empty' },
    errors: { 409: ['row_conflict', 'session_state', 'document_trashed'], 422: ['invalid_body'] },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's1',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        request: {
            params: { ...session3, itemId: ids.sessionItemChapelNote.id },
            query: { baseRv: 1 },
        },
        response: null,
        consumes: true,
    },
});
/** Who is at the table: one row per player character. */
export const sessionsAttendanceGet = defineRoute({
    id: 'sessions.attendance.get',
    method: 'GET',
    path: '/api/sessions/:id/attendance',
    params: DocIdParams,
    response: { kind: 'json', schema: SessionAttendanceV1 },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's1',
    examples: { request: { params: session3 }, response: EXAMPLE_SESSION_ATTENDANCE },
});
/**
 * Replace the attendance with the set sent. A player character is an owner pointer: a reference
 * document is never at the table.
 */
export const sessionsAttendancePut = defineRoute({
    id: 'sessions.attendance.put',
    method: 'PUT',
    path: '/api/sessions/:id/attendance',
    params: DocIdParams,
    body: SessionAttendancePutV1,
    response: { kind: 'json', schema: SessionAttendanceV1 },
    errors: { 409: ['session_state', 'document_trashed'], 422: ['invalid_body'] },
    reference: 'deny',
    docIds: { owner: [...SESSION, 'body.rows[].pcId'] },
    ...sessions,
    stage: 's1',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        request: { params: session3, body: { rows: [{ pcId: ids.pcTamsinReed.id, present: true }] } },
        response: EXAMPLE_SESSION_ATTENDANCE,
        consumes: true,
    },
});
/** A session's captures by `seq` (a bounded list of `SESSION_CAPTURES_MAX`), live ones only. */
export const sessionsCapturesList = defineRoute({
    id: 'sessions.captures.list',
    method: 'GET',
    path: '/api/sessions/:id/captures',
    params: DocIdParams,
    query: SessionCapturesQueryV1,
    response: { kind: 'json', schema: listOf(SessionCaptureV1) },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's1',
    examples: {
        request: { params: session3, query: { status: 'open' } },
        response: { items: [EXAMPLE_SESSION_CAPTURE], next: null },
    },
});
/** Capture during or after a session; the server assigns `seq`. */
export const sessionsCapturesCreate = defineRoute({
    id: 'sessions.captures.create',
    method: 'POST',
    path: '/api/sessions/:id/captures',
    params: DocIdParams,
    body: SessionCaptureCreateV1,
    response: { kind: 'json', schema: SessionCaptureV1, status: 201 },
    errors: { 409: ['session_state', 'document_trashed'], 422: ['invalid_body'] },
    reference: 'target-ok',
    docIds: { owner: SESSION, target: ['body.docIds[]'] },
    ...sessions,
    stage: 's1',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        request: {
            params: { id: ids.sessionSaltInTheWells.id },
            body: {
                kind: 'decision',
                body: 'The party sided with the well-keepers.',
                docIds: [ids.itemTidebell.id],
            },
        },
        response: EXAMPLE_SESSION_CAPTURE_CREATED,
    },
});
/** Edit a capture's kind, text, documents or in-world time. */
export const sessionsCapturesUpdate = defineRoute({
    id: 'sessions.captures.update',
    method: 'PATCH',
    path: '/api/sessions/:id/captures/:captureId',
    params: SessionCaptureParams,
    body: SessionCaptureUpdateV1,
    response: { kind: 'json', schema: SessionCaptureV1 },
    errors: { 409: ['row_conflict', 'session_state', 'document_trashed'], 422: ['invalid_body'] },
    reference: 'target-ok',
    docIds: { owner: SESSION, target: ['body.docIds[]'] },
    ...sessions,
    stage: 's1',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        // Session 2 (played): captures are writable only while a session runs or once it is played
        // (2.C's `WRITABLE`), so session 3's loot (prepped) would answer 409 (DECISIONS 490).
        request: {
            params: { id: ids.sessionSaltInTheWells.id, captureId: ids.captureSaltStub.id },
            body: {
                baseRv: 1,
                body: 'The ferryman who saw the Tidebell go under.',
                docIds: [ids.itemTidebell.id],
            },
        },
        response: EXAMPLE_SESSION_CAPTURE_UPDATED,
        consumes: true,
    },
});
/**
 * Remove a capture (soft: `deleted_at`). A capture carries `rv` from migration 0014, so the delete
 * takes `?baseRv=` as every delete of a versioned row does (1.0b conventions, "Writes").
 */
export const sessionsCapturesRemove = defineRoute({
    id: 'sessions.captures.remove',
    method: 'DELETE',
    path: '/api/sessions/:id/captures/:captureId',
    params: SessionCaptureParams,
    query: BaseRvQueryV1,
    response: { kind: 'empty' },
    errors: { 409: ['row_conflict', 'session_state', 'document_trashed'], 422: ['invalid_body'] },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's1',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        // Session 2's open stub: session 3 is prepped, where captures are not writable (DECISIONS 490).
        request: {
            params: { id: ids.sessionSaltInTheWells.id, captureId: ids.captureSaltStub.id },
            query: { baseRv: 1 },
        },
        response: null,
        consumes: true,
    },
});
/**
 * Start the session: its number (the next free one when absent, decision 15), `running`,
 * `started_at`, and its placement starting at NOW (2.C s2). One session runs at a time.
 */
export const sessionsStart = defineRoute({
    id: 'sessions.start',
    method: 'POST',
    path: '/api/sessions/:id/start',
    params: DocIdParams,
    body: SessionStartV1,
    response: { kind: 'json', schema: SessionV1 },
    errors: { 409: ['session_state'], 422: ['invalid_body'] },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's2',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        request: { params: session3, body: {} },
        response: EXAMPLE_SESSION_STARTED,
        consumes: true,
    },
});
/** End the running session: `played`, `ended_at`, its placement ending at NOW. */
export const sessionsEnd = defineRoute({
    id: 'sessions.end',
    method: 'POST',
    path: '/api/sessions/:id/end',
    params: DocIdParams,
    response: { kind: 'json', schema: SessionV1 },
    errors: { 409: ['session_state'] },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's2',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        request: { params: session3 },
        response: EXAMPLE_SESSION_ENDED,
        consumes: true,
        success: NEEDS_RUNNING_SESSION,
    },
});
/** Finish a played session: `processed`, and the next session planned at NOW. */
export const sessionsFinish = defineRoute({
    id: 'sessions.finish',
    method: 'POST',
    path: '/api/sessions/:id/finish',
    params: DocIdParams,
    response: { kind: 'json', schema: SessionFinishedV1 },
    errors: { 409: ['session_state'] },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's2',
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        request: { params: { id: ids.sessionSaltInTheWells.id } },
        response: EXAMPLE_SESSION_FINISHED,
        consumes: true,
    },
});
/**
 * Tonight: the given session, or the next one; `smoke: 'read'` so the smoke suite may call it
 * (TS r6b's SMOKE v2b; DECISIONS 215 ruling 45).
 */
export const sessionsTonight = defineRoute({
    id: 'sessions.tonight',
    method: 'GET',
    path: '/api/tonight',
    query: TonightQueryV1,
    response: { kind: 'json', schema: TonightV1 },
    smoke: 'read',
    reference: 'deny',
    docIds: { owner: ['query.sessionId'] },
    ...sessions,
    stage: 's2',
    examples: {
        request: { query: { sessionId: ids.sessionSunkenChapel.id } },
        response: EXAMPLE_TONIGHT,
    },
});
// ---------------------------------------------------------- CS r5b: 2.C s3's five routes
const session2 = { id: ids.sessionSaltInTheWells.id };
/**
 * Close an open capture by one action (CS r5b deliverable 164; 2.C s3): a stub page, a link, a
 * reveal confirmed or rejected, a canon event, a clock advance, or a dismissal; one batch. The
 * action's document is a target (an SRD page may be linked).
 */
export const sessionsCapturesPromote = defineRoute({
    id: 'sessions.captures.promote',
    method: 'POST',
    path: '/api/sessions/:id/captures/:captureId/promote',
    params: SessionCaptureParams,
    body: CapturePromoteV1,
    response: { kind: 'json', schema: CapturePromotedV1 },
    errors: { 409: ['row_conflict', 'session_state', 'document_trashed'], 422: ['invalid_body'] },
    reference: 'target-ok',
    docIds: { owner: SESSION, target: ['body.action.documentId'] },
    ...sessions,
    stage: 's3',
    changeBatch: true,
    examples: {
        request: {
            params: { ...session2, captureId: ids.captureSaltStub.id },
            body: { baseRv: 1, action: { kind: 'link', documentId: ids.npcOldSef.id } },
        },
        response: EXAMPLE_CAPTURE_PROMOTED,
        consumes: true,
    },
});
/** A session's source files, newest first (a bounded list of `SESSION_SOURCES_MAX`). */
export const sessionsSourcesList = defineRoute({
    id: 'sessions.sources.list',
    method: 'GET',
    path: '/api/sessions/:id/sources',
    params: DocIdParams,
    response: { kind: 'json', schema: listOf(SessionSourceV1, SESSION_SOURCES_MAX) },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's3',
    examples: {
        request: { params: session2 },
        response: { items: [EXAMPLE_SOURCE_TRANSCRIPT, EXAMPLE_SOURCE_SUMMARY], next: null },
    },
});
/**
 * Upload a source file (multipart: `file` and `kind`), at most 25 MiB (413). The binder answers
 * 415 for a media type outside `SESSION_SOURCE_MEDIA_TYPES`, the server for an extension outside
 * the kind's `SESSION_SOURCE_EXTENSIONS` (nothing is sniffed). The same bytes again answer the
 * session's existing source. Such a success wrote nothing: it opens no change batch and takes no
 * `CHANGE_BATCH_HEADER`, so no Undo is offered for it (CS r6c addendum item 1; 2.C s3).
 */
export const sessionsSourcesUpload = defineRoute({
    id: 'sessions.sources.upload',
    method: 'POST',
    path: '/api/sessions/:id/sources',
    params: DocIdParams,
    requestKind: 'multipart',
    upload: {
        field: 'file',
        maxBytes: SESSION_SOURCE_MAX_BYTES,
        mimeTypes: SESSION_SOURCE_MEDIA_TYPES,
    },
    body: SessionSourceUploadFieldsV1,
    response: { kind: 'json', schema: SessionSourceV1, status: 201 },
    errors: { 409: ['session_state', 'document_trashed'], 422: ['invalid_body'] },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's3',
    changeBatch: true,
    examples: {
        request: { params: session2, body: { kind: 'transcript' }, file: EXAMPLE_TRANSCRIPT_FILE },
        response: EXAMPLE_SOURCE_UPLOADED,
        consumes: true,
    },
});
/** Remove a source (soft: `deleted_at`); its stored file is kept. */
export const sessionsSourcesRemove = defineRoute({
    id: 'sessions.sources.remove',
    method: 'DELETE',
    path: '/api/sessions/:id/sources/:sourceId',
    params: SessionSourceParams,
    query: BaseRvQueryV1,
    response: { kind: 'empty' },
    errors: { 409: ['row_conflict', 'session_state', 'document_trashed'] },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's3',
    changeBatch: true,
    examples: {
        request: {
            params: { ...session2, sourceId: ids.sourceSaltTranscript.id },
            query: { baseRv: 1 },
        },
        response: null,
        consumes: true,
    },
});
/**
 * A session's tentative assertions that nothing supersedes, each with a label, its document and
 * whether it was confirmed or rejected since (2.E's Undo after a reload, 2.F's After tab). Read
 * only.
 */
export const sessionsRevealsList = defineRoute({
    id: 'sessions.reveals.list',
    method: 'GET',
    path: '/api/sessions/:id/reveals',
    params: DocIdParams,
    response: { kind: 'json', schema: listOf(SessionRevealV1, SESSION_REVEALS_MAX) },
    reference: 'deny',
    docIds: { owner: SESSION },
    ...sessions,
    stage: 's3',
    examples: {
        request: { params: session2 },
        response: { items: [...EXAMPLE_SESSION_REVEALS], next: null },
    },
});
//# sourceMappingURL=sessions-2b.js.map